import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserMenu } from '@app/features/users/user-menu/user-menu';
import { User, UserRole } from '@app/features/users/user.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import {
  CommentDialog,
  CommentDialogData,
} from '@app/shared/dialogs/comment-dialog/comment-dialog';
import { ValidationCircuits } from '@app/features/contracts/validation/validation-circuit';
import { ValidationSubjectItem } from '@app/features/contracts/validation/validation-subject.model';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { filter, map, switchMap, tap } from 'rxjs';
import {
  REQUEST_VALIDATION_TO_ROLE,
  ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO,
  STATUS_ALLOWED_FOR_VALIDATION_ACTION,
} from '../validation-request-service';
import { ValidationRequestTypeLabelPipe } from '../validation-request-type-label-pipe';
import { VALIDATION_REQUEST_TYPES, ValidationRequestType } from '../validation-request.model';

@Component({
  selector: 'app-button-accept-and-request-validation',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    UserMenu,
    MatMenuModule,
    ValidationRequestTypeLabelPipe,
  ],
  templateUrl: './button-accept-and-request-validation.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./button-accept-and-request-validation.scss'],
})
export class ButtonAcceptAndRequestValidation extends RxjsDirective {
  /** Le sujet validé : un contrat d'embauche, ou un avenant. */
  contract = input.required<ValidationSubjectItem>();
  private readonly validationCircuits = inject(ValidationCircuits);
  protected circuit = () => this.validationCircuits.of(this.contract());

  acceptValidationType = input.required<ValidationRequestType>();
  validationAccepted = output<void>();
  validationRequested = output<void>();

  authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);
  dialog = inject(MatDialog);
  validationRequestPipe = new ValidationRequestTypeLabelPipe();

  nextValidationType?: ValidationRequestType;
  rolesAllowedToByPassRequestedTo: UserRole[] = [];
  requestToRoles: UserRole[] = [];

  constructor() {
    super();
    effect(() => {
      this.rolesAllowedToByPassRequestedTo =
        ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO[this.acceptValidationType()] ?? [];
      this.initNextValidationTypeAndRequestToRoles();
    });
  }

  private initNextValidationTypeAndRequestToRoles() {
    const currentIndex = VALIDATION_REQUEST_TYPES.indexOf(this.acceptValidationType());
    if (!(currentIndex >= 0 && currentIndex + 1 < VALIDATION_REQUEST_TYPES.length)) {
      console.warn(
        `Current validation type ${this.acceptValidationType()} is not in VALIDATION_REQUEST_TYPES or is the last one, cannot determine nextValidationType`,
      );
      this.nextValidationType = undefined;
      this.requestToRoles = [];
      return;
    }

    this.nextValidationType = VALIDATION_REQUEST_TYPES[currentIndex + 1];
    this.requestToRoles = REQUEST_VALIDATION_TO_ROLE[this.nextValidationType] ?? [];
  }

  get canAcceptValidation(): boolean {
    if (this.contract().status === 'DRH_VALIDATING') {
      return false;
    }
    const validationRequests = this.contract().validationRequests ?? [];
    const allowedStatuses = STATUS_ALLOWED_FOR_VALIDATION_ACTION[this.acceptValidationType()] ?? [];
    const pendingValidationRequests = validationRequests.filter(
      (req) => req.type === this.acceptValidationType() && req.status === 'PENDING',
    );
    const hasPendingValidationRequest = pendingValidationRequests.length > 0;
    const isRequestedToCurrentUser = pendingValidationRequests.some(
      (req) => req.requestedTo === this.authenticatedUserService.userId,
    );
    const isBypassRole = this.rolesAllowedToByPassRequestedTo.some((role) =>
      this.authenticatedUserService.realmRoles.includes(role),
    );

    return (
      !!this.nextValidationType &&
      allowedStatuses.includes(this.contract().status) &&
      hasPendingValidationRequest &&
      (isRequestedToCurrentUser || isBypassRole)
    );
  }

  acceptAndRequestValidation(user: User): void {
    this.initNextValidationTypeAndRequestToRoles();

    if (!this.nextValidationType) {
      console.error('Impossible de déterminer le type de validation suivant');
      return;
    }

    const nextValidationType = this.nextValidationType;

    this.register(
      this.circuit()
        .verify(this.contract().id)
        .pipe(
          switchMap(() => {
            const dialogRef = this.dialog.open<
              CommentDialog,
              CommentDialogData,
              string | undefined
            >(CommentDialog, {
              data: {
                title:
                  'Accepter et demander la validation ' +
                  this.validationRequestPipe.transform(nextValidationType),
                required: false,
              },
              minWidth: '700px',
            });
            return dialogRef.afterClosed();
          }),
          filter((comment: string | undefined): comment is string => comment !== undefined),
          switchMap((comment: string) =>
            this.circuit()
              .accept(this.contract().id, false)
              .pipe(map(() => comment)),
          ),
          switchMap((comment: string) => {
            return this.circuit().request(this.contract().id, user.id, nextValidationType, comment);
          }),
          tap(() => {
            this.validationAccepted.emit();
            this.validationRequested.emit();
          }),
        )
        .subscribe(),
    );
  }
}
